// ============ SCRIPT SCHEMA TYPES ============

/** Condition for visibility/activation logic */
export interface ActivatedByCondition {
  Key: string;
  Operator: 'HasValue' | 'HasNoValue' | 'Eq' | 'Neq';
  Values?: string[];
  Type: 'Question' | 'Luggage';
  ActivatedBy?: ActivatedByCondition[];
}

/** Validation rules on a question */
export interface QuestionValidation {
  min?: { value: number | string; message?: string; error?: string };
  max?: { value: number | string; message?: string; error?: string };
  pattern?: { value: string; message?: string; error?: string };
}

/** An answer option (for select-type questions) */
export interface ScriptAnswer {
  Key: string;
  key?: string;
  outputText?: string;
  Sort?: number;
  sort?: number;
  Default?: string;
  default?: boolean;
  selected?: boolean;
  ActivatedBy?: ActivatedByCondition[];
}

/** Dynamic answer configuration */
export interface DynamicConfig {
  Method: string;
  Parameters?: DynamicParameter[];
}

export interface DynamicParameter {
  Name: string;
  Value: string;
  Type: 'Question' | 'Luggage' | 'Constant' | 'APIPlugIn';
}

/** Prepopulate configuration — matches AngularJS question.PrePopulate.PrePopulatedBy */
export interface PrePopulateConfig {
  PrePopulatedBy: {
    Key: string;
    Type: 'Luggage' | 'Question';
  };
}

/** A question (field) in the form */
export interface ScriptQuestion {
  Name: string;
  Type: QuestionType;
  Required?: string;
  required?: boolean;
  Visible?: string;
  Hidden?: string;
  hidden?: boolean;
  visible?: boolean;
  ReadOnly?: boolean;
  Transport?: string;
  Seed?: string;
  Answers?: ScriptAnswer[];
  originalAnswerSet?: ScriptAnswer[];
  Validation?: QuestionValidation;
  Dynamic?: DynamicConfig;
  PrePopulate?: PrePopulateConfig;
  ActivatedBy?: ActivatedByCondition[];
  affects?: ScriptEntity[];
  hash?: string;
  sort?: number;
}

/** A question group containing questions */
export interface ScriptQuestionGroup {
  Name: string;
  Questions: ScriptQuestion[];
  Visible?: string;
  Hidden?: string;
  hidden?: boolean;
  visible?: boolean;
  Transport?: string;
  Seed?: string;
  VisibleOnPreAnswer?: string;
  ActivatedBy?: ActivatedByCondition[];
  affects?: ScriptEntity[];
  hash?: string;
  sort?: number;
}

/** A station (page/section) containing question groups */
export interface ScriptStation {
  Name: string;
  QuestionGroups: ScriptQuestionGroup[];
  Visible?: string;
  Hidden?: string;
  hidden?: boolean;
  visible?: boolean;
  Transport?: string;
  Seed?: string;
  ActivatedBy?: ActivatedByCondition[];
  affects?: ScriptEntity[];
  hash?: string;
  sort?: number;
  unansweredRequiredQuestions?: number;
}

/** Union type for any script entity (station, question group, or question) */
export type ScriptEntity = ScriptStation | ScriptQuestionGroup | ScriptQuestion;

/** The full script definition */
export interface Script {
  Name: string;
  Stations: ScriptStation[];
  Version?: string;
  version?: string;
}

// ============ QUESTION TYPES ============

export type QuestionType =
  | 'Station'
  | 'QuestionGroup'
  | 'SingleSelect'
  | 'SingleSelectFilter'
  | 'MultiSelect'
  | 'TextField'
  | 'MultiLineText'
  | 'Date'
  | 'DateTime'
  | 'Numeric'
  | 'Currency'
  | 'Phone';

// ============ ANSWER STORAGE ============

export interface StoredAnswerValue {
  key?: string;
  value?: string | number | Date;
}

export interface StoredAnswer {
  answers: StoredAnswerValue[];
  active: boolean;
  questionType: string;
  transport: string;
  empty?: boolean;
}

/** currentlySelectedAnswers[questionName][hash] = StoredAnswer */
export type AnswerStore = Record<string, Record<string, StoredAnswer>>;

// ============ LUGGAGE ============

export interface LuggageAnswer {
  key?: string;
  value?: string;
}

export interface LuggageEntry {
  answers: LuggageAnswer[];
}

export type LuggageData = Record<string, LuggageEntry>;

// ============ REQUEST CONTEXT ============

export interface RequestContext {
  contextId?: string;
  contextKey?: string;
  requestType?: string;
}

// ============ TRANSPORT (SUBMISSION) ============

export interface TransportAnswer {
  Name: string;
  Values: string[];
  KeyValues: { Key: string | null; Value: string | null }[];
  QuestionType: string | null;
  MediaGroup: null;
  Asset: null;
  QuestionText: null;
  DataType: null;
  TermsetName: null;
  GroupKey: null;
}

export interface TransportWorkOrder {
  SPIPropertyId: number;
  OrderNumber: number;
  Script: null;
  ScriptVersion: null;
  ThirdPartyGuid: null;
  SubmittedDate: null;
  TimeZone: null;
  WorkCode: null;
  ClientCode: null;
  LoanType: null;
  SubmittingResource: { code: string } | null;
}

export interface TransportObject {
  Answers?: TransportAnswer[];
  WorkOrder: TransportWorkOrder;
  Bids: unknown[];
  Tasks: unknown[];
  Damages: unknown[];
  Access: null;
  [key: string]: unknown;
}

// ============ CONSTANTS ============

export const REQUEST_TYPE = {
  ORDER_TYPE: 'OrderRequest',
  PROPERTY_TYPE: 'PropertyRequest',
  TEXT_TYPE: 'TextRequest',
  VENDOR_REASSIGN_TYPE: 'VendorReassign',
  AUDIT_REQUEST_TYPE: 'AuditRequest',
  POST_AUDIT_REQUEST_TYPE: 'PostAuditRequest',
  ICC_STATUS_TYPE: 'ICCStatusRequest',
  ICC_CONFIRMATION_TYPE: 'ICCConfirmationRequest',
  ORDER_MODIFICATION_TYPE: 'OrderModificationRequest',
  VENDOR_TEXT_TYPE: 'VendorTextRequest',
  RECONVEY_REQUEST_TYPE: 'ReconveyRequest',
  CONVEY_IMPEDIMENTS_TYPE: 'ConveyImpediments',
  CODE_VIOLATION_TYPE: 'CodeViolationRequest',
  SCHEDULE_TYPE: 'ScheduleScriptRequest',
} as const;

export const TRANSPORT = {
  DEFAULT: 'Answers',
  EXCLUDE: 'Exclude',
} as const;

export const CONTEXT = {
  WORK_ORDER: 'WorkOrder.OrderNumber',
} as const;

export const SCRIPT_NAME = {
  PROPERTY_REQUEST: 'PropertyRequest',
  TEXT_REQUEST: 'TextRequest',
  ORDER_REQUEST: 'OrderRequest',
  VENDOR_REASSIGN_REQUEST: 'VendorReassignRequest',
  IL_SCHEDULE: 'InsuranceLossSchedule',
  AUDIT_REQUEST: 'AuditRequest',
  ICC_STATUS_REQUEST: 'ICCStatusRequest',
  ICC_CONFIRMATION_REQUEST: 'ICCConfirmationRequest',
  ORDER_MODIFICATION_REQUEST: 'OrderModificationRequest',
  VENDOR_TEXT_REQUEST: 'VendorTextRequest',
  RECONVEY_REQUEST: 'ReconveyRequest',
  CONVEY_IMPEDIMENTS_REQUEST: 'ConveyImpediments',
  CODE_VIOLATION_TYPE: 'CodeViolation',
  SACC: 'SACC',
} as const;

export const QUESTION_TYPES_OUTPUT_MAP: Record<string, string> = {
  SingleSelectFilter: 'SingleSelect',
  DateTime: 'Date',
};

export const KEY_TYPES = new Set(['MultiSelect', 'SingleSelectFilter', 'SingleSelect']);
